// Step 1 — Post type / category. Inserted ahead of the Platform step so the
// user declares intent before scrolling platforms. Three options:
//   - Single Social Media Post  (most common; any 1-frame social platform)
//   - Carousel Social Media Post (4-frame Instagram Carousel specifically)
//   - Product Shot               (standalone hero shot; no platform branding)
//
// Today this only captures the choice in wizard state — downstream steps
// (Platform, Upload, etc.) still run the same way regardless of pick. The
// idea is to get the UI shipped and observe how users naturally proceed,
// then layer in filtering / auto-advance behavior in a follow-up.

import { useNavigate } from 'react-router-dom';
import { useCredits } from '@/hooks/useCredits';
import StepHeader from './StepHeader';

export type PostType = 'single' | 'carousel' | 'product-shot' | 'fashion' | 'product-swap';

export interface Category {
  id: PostType;
  label: string;
  description: string;
  icon: string;          // Remix Icon class for the card tile
  previewSrc: string;    // sample image path under /public — front of the fan deck
  /** Optional 3-image fan deck for the /create dashboard. [front, mid, back].
   *  When omitted, the tile falls back to a flat single-image preview. */
  fanImages?: [string, string, string];
  badge?: string;        // optional label overlaid on the preview (e.g. "Multi")
  /** When true, the card is paid-tier-only. Free users see a lock
   *  overlay and clicking routes to /checkout instead of selecting. */
  paidOnly?: boolean;
}

// Exported so the /create dashboard can reuse the same tile data without
// duplicating it. Single source of truth for tile content + previews.
export const CATEGORIES: Category[] = [
  {
    id: 'product-shot',
    label: 'Product Shot',
    description:
      'A clean, studio-style hero image of your product. No platform branding — just a beautifully rendered shot for ads, websites, or print.',
    icon: 'ri-camera-lens-line',
    previewSrc: '/dashboard-tiles/product-shot.webp',
    fanImages: [
      '/category-previews/product-shot.png',
      '/category-previews/product-shot-2.png',
      '/category-previews/product-shot-3.png',
    ],
  },
  {
    id: 'product-swap',
    // 'In Hand' is the locked user-facing name (Jun 11 — was "Product
    // Swap" here, "Held Product" on /appv3). Internal id stays
    // 'product-swap' for routing/backend continuity.
    label: 'In Hand',
    description:
      'Place your product into a model’s hand. Pick a scene where a model holds a product, and we swap in yours — bottles, jars, tubes, supplements.',
    icon: 'ri-hand-heart-line',
    previewSrc: '/templates/template26/frame1_thumb.png',
    fanImages: [
      '/templates/template26/frame1_thumb.png',
      '/category-previews/product-swap-2.png',
      '/category-previews/product-swap-3.png',
    ],
    badge: 'NEW',
  },
  {
    id: 'fashion',
    label: 'Model Shot',
    description:
      'Show your product on a real human model — worn like apparel or held like an accessory. AI invents the model + scene; your product stays the hero.',
    icon: 'ri-user-line',
    previewSrc: '/dashboard-tiles/model-shot.webp',
    fanImages: [
      '/category-previews/fashion.png',
      '/category-previews/fashion-2.png',
      '/category-previews/fashion-3.png',
    ],
  },
  {
    id: 'carousel',
    label: 'Carousel',
    description:
      'Four connected frames that swipe horizontally. Great for stories, tutorials, and step-by-step product showcases on Instagram.',
    icon: 'ri-stack-line',
    previewSrc: '/category-previews/carousel.png',
    fanImages: [
      '/category-previews/carousel.png',
      '/category-previews/carousel-2.png',
      '/category-previews/carousel-3.png',
    ],
    badge: 'Multi · 4 frames',
  },
  {
    id: 'single',
    label: 'Social Post',
    description:
      'One image, one platform. Perfect for standalone announcements, daily posts, or quick engagement on any social channel.',
    icon: 'ri-image-line',
    previewSrc: '/category-previews/single-social.png',
    fanImages: [
      '/category-previews/single-social.png',
      '/category-previews/single-social-2.png',
      '/category-previews/single-social-3.png',
    ],
  },
];

interface StepCategoryProps {
  selected: PostType | null;
  onSelect: (id: PostType) => void;
  /** /appv2's Section wrapper provides its own header — skip the inner
   *  StepHeader and the h-full layout constraint when set. */
  compact?: boolean;
}

export default function StepCategory({ selected, onSelect, compact }: StepCategoryProps) {
  const navigate = useNavigate();
  const { planTier } = useCredits();
  const isFree = planTier === 'free';

  return (
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {!compact && (
        <StepHeader
          icon="ri-stack-line"
          title="What are you creating?"
          subtitle="Pick the type of content you want to generate. This shapes the platform options and output in the next steps."
        />
      )}

      {/* Full-width grid — cards stretch across the wizard panel. The
          internal element sizes (icon tile, title, description, preview
          aspect) are sized compact so the cards read as informational
          tiles rather than oversized hero cards (cf. competitor layouts
          where each card has roomy padding around small content). */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
        {CATEGORIES.map((cat) => {
          const isActive = selected === cat.id;
          // paidOnly is currently unused (the Video card was the only
          // paid-only category; removed May 26). Hook kept in place so a
          // future paid feature card can drop straight in.
          const isLocked = !!cat.paidOnly && isFree;
          const handleClick = () => {
            if (isLocked) {
              navigate('/checkout');
              return;
            }
            onSelect(cat.id);
          };
          return (
            <button
              key={cat.id}
              onClick={handleClick}
              title={isLocked ? 'Upgrade to a paid plan to unlock this feature' : undefined}
              className={`relative text-left p-6 rounded-2xl border-2 transition-all bg-white cursor-pointer ${
                isActive
                  ? 'border-primary shadow-md shadow-primary/10'
                  : isLocked
                    ? 'border-surface-dark/30 hover:border-amber-400/60 hover:shadow-md hover:shadow-amber-500/10'
                    : 'border-surface-dark/30 hover:border-primary/40 hover:bg-primary/[0.02]'
              }`}
            >
              {/* Locked overlay — sits on TOP of the preview image with
                  a soft frost so the user can still see what they'd be
                  getting, but the lock icon makes the upsell obvious. */}
              {isLocked && (
                <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold shadow-sm z-10">
                  <i className="ri-lock-2-line text-xs" />
                  Upgrade
                </div>
              )}
              {/* Selected check — top-right corner. Suppress when
                  locked (Upgrade pill takes that spot). */}
              {isActive && !isLocked && (
                <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary flex items-center justify-center z-10">
                  <i className="ri-check-line text-white text-xs" />
                </div>
              )}

              {/* Icon tile — compact (was w-12 → w-9) so the title carries
                  the visual hierarchy. */}
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center mb-4 transition-colors ${
                  isActive
                    ? 'bg-primary text-white'
                    : 'bg-primary/10 text-primary'
                }`}
              >
                <i className={`${cat.icon} text-base`} />
              </div>

              {/* Title — was text-base, now text-sm-bold-display for tighter
                  proportions on wider cards. */}
              <h3 className="font-display font-bold text-dark text-sm mb-1.5 leading-tight">
                {cat.label}
              </h3>

              {/* Description — was text-sm, now text-xs so the bulk of card
                  height goes to the preview image (matches competitor cards
                  where description is a brief gloss above the visual). */}
              <p className="text-dark-muted text-xs leading-relaxed mb-4">
                {cat.description}
              </p>

              {/* Example image — let the source image dictate its own
                  height. Each category's preview has a different native
                  aspect (Fashion is tall portrait, Carousel is landscape,
                  Product Shot is square-ish), and these are sales-quality
                  visuals — cropping them defeats the point. Cards may
                  end up slightly different heights as a result; the grid
                  accommodates that. */}
              <div className="relative rounded-xl overflow-hidden border border-surface-dark/20 bg-surface">
                <img
                  src={cat.previewSrc}
                  alt={`${cat.label} example`}
                  className="w-full h-auto block"
                  loading="lazy"
                />
                {cat.badge && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary text-white text-[10px] font-bold shadow-md">
                    {cat.badge}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
